import type { ItemFieldRow } from './item-field-reader';
import { readItemField } from './item-field-reader';
import type { SkillTierCatalogEntry } from '../types';
import { readLayerListEntryUuid } from './inventory-raw-readers';

type Row = Record<string, unknown>;
const record = (value: unknown): value is Row => value !== null && typeof value === 'object';
const uuid = (value: string): boolean => /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(value);

export interface SkillTierGrantReaders {
    tiers: readonly SkillTierCatalogEntry[];
    entries: (row: ItemFieldRow, key: string) => readonly unknown[];
    /** NPC pre-reference compilation supplies its own-layer reader here. */
    containerEntries?: (key: string) => readonly unknown[];
    definition: (id: string) => ItemFieldRow | null;
    /** Caller supplies native level/source and runtime gates. Initial callers
     * have already filtered level requirements; delta callers must retain them. */
    eligible: (ability: ItemFieldRow, entry: unknown) => boolean;
}
export interface SkillTierGrant {
    key: string;
    level: number;
    /** Source order is feat, passive, then the order in each held container. */
    sources: readonly { abilityId: string; sourceId: string; statusId: string }[];
}
export interface SkillTierGrantFold {
    best: ReadonlyMap<string, SkillTierGrant>;
    grants: Row;
    uuids: string;
    modeSensitive: boolean;
    selfGrants: readonly { abilityId: string; skillId: string; required: number; granted: number }[];
}

/** One native owner for initial and held-row grants. Always read definitions and
 * conditions: unchanged UUIDs do not mean unchanged definitions or eligibility.
 * No caches, actor copies, output restoration or complete compiler calls. */
export function foldSkillTierGrants(host: ItemFieldRow, deps: SkillTierGrantReaders): SkillTierGrantFold {
    const best = new Map<string, SkillTierGrant>();
    const selfGrants: { abilityId: string; skillId: string; required: number; granted: number }[] = [];
    const warned = new Set<string>();
    const containers = deps.containerEntries ?? (key => deps.entries(host, key));
    let uuids = '', modeSensitive = false;
    for (const key of ['feat', 'passive_ability']) for (const entry of containers(key)) {
        const id = readLayerListEntryUuid(entry);
        uuids += '|' + id;
        const ability = record(entry) && record(entry.item_arr) ? entry.item_arr
            : uuid(id) ? deps.definition(id) : null;
        if (!ability) continue;
        modeSensitive ||= deps.tiers.some(tier => deps.entries(ability,
            tier.variable_name.replace(/^skill_tier_/, 'requires_skill_tier_')).length > 0);
        const tiers = deps.tiers.filter(tier => tier.skill_tier_level > 0).map(tier => ({
            tier, cells: deps.entries(ability, tier.variable_name) }));
        if (!tiers.some(tier => tier.cells.length)) continue;
        const source = { abilityId: id || String(readItemField(ability, 'item_id') ?? ''),
            sourceId: record(entry) ? String(readItemField(entry, 'source') ?? id) : id,
            statusId: record(entry) ? String(readItemField(entry, 'granted_by_status_id') ?? '') : '' };
        const candidates: { skill: string; key: string; level: number }[] = [];
        for (const { tier, cells } of tiers) {
            for (const cell of cells) {
                const skill = readLayerListEntryUuid(cell);
                if (!uuid(skill)) continue;
                const required = Math.max(0, ...deps.tiers.filter(requirement => deps.entries(ability,
                    requirement.variable_name.replace(/^skill_tier_/, 'requires_skill_tier_'))
                    .some(entry => readLayerListEntryUuid(entry).toUpperCase() === skill.toUpperCase()))
                    .map(requirement => requirement.skill_tier_level));
                const warningKey = source.abilityId.toUpperCase() + '|' + skill.toUpperCase();
                if (required > 0 && tier.skill_tier_level >= required && !warned.has(warningKey)) {
                    warned.add(warningKey);
                    selfGrants.push({ abilityId: source.abilityId, skillId: skill, required, granted: tier.skill_tier_level });
                }
                candidates.push({ skill, key: tier.variable_name, level: tier.skill_tier_level });
            }
        }
        // Diagnose circular content even while its runtime conditions are false.
        if (!deps.eligible(ability, entry)) continue;
        for (const { skill, key, level } of candidates) {
            const previous = best.get(skill);
            if (!previous || previous.level < level) best.set(skill, { key, level, sources: [source] });
            else if (previous.level === level && !previous.sources.some(old => sameSource(old, source)))
                best.set(skill, { ...previous, sources: [...previous.sources, source] });
        }
    }
    const grants: Row = {};
    for (const [id, tier] of best) ((grants[tier.key] ??= []) as unknown[]).push({ items: { item_id: id } });
    return { best, grants, uuids, modeSensitive, selfGrants };
}

const sameSource = (a: SkillTierGrant['sources'][number], b: SkillTierGrant['sources'][number]): boolean =>
    a.abilityId === b.abilityId && a.sourceId === b.sourceId && a.statusId === b.statusId;

/** Reconcile the grant store and visit only changed skill rows. The caller owns
 * its native skill-entry projection and any affected integer/essence families.
 * Independent grants are folded together before a removed source is retracted. */
export function applySkillTierGrantDelta(host: Row, previous: SkillTierGrantFold, next: SkillTierGrantFold,
    project: (id: string, grant: SkillTierGrant | undefined) => void): string[] {
    const computed = (host.computed ??= {}) as Row, creature = (computed.creature ??= {}) as Row;
    const old = record(creature.skill_tier_grants) ? creature.skill_tier_grants : {};
    for (const key of new Set([...Object.keys(old), ...Object.keys(next.grants)])) {
        const before = record(old[key]) ? Object.values(old[key]).map(readLayerListEntryUuid) : [];
        const after = record(next.grants[key]) ? Object.values(next.grants[key]).map(readLayerListEntryUuid) : [];
        if (before.length === after.length && before.every((id, i) => id === after[i])) continue;
        if (after.length) old[key] = next.grants[key]; else delete old[key];
    }
    if (Object.keys(old).length) creature.skill_tier_grants = old; else delete creature.skill_tier_grants;
    const changed: string[] = [];
    for (const id of new Set([...previous.best.keys(), ...next.best.keys()])) {
        const before = previous.best.get(id), after = next.best.get(id);
        if (before?.key === after?.key && before?.level === after?.level
            && before?.sources.length === after?.sources.length
            && before?.sources.every((source, i) => after?.sources[i] !== undefined && sameSource(source, after.sources[i]!))) continue;
        changed.push(id);
        project(id, after);
    }
    return changed;
}
